<template>
  <div v-bind="rootProps" :class="classes">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { computed, type HTMLAttributes } from 'vue'
import { reactiveOmit } from '@vueuse/core'

import type { IconCircleVariant } from './types'

export type IconCircleProps = {
  /**
   * Variant of the icon. It will set the color and background color.
   * @type {IconCircleVariant}
   * @example 'success'
   * @default 'success'
   */
  variant?: IconCircleVariant
  // eslint-disable-next-line vue/prop-name-casing
} & /* @vue-ignore */ HTMLAttributes

const props = withDefaults(defineProps<IconCircleProps>(), {
  variant: 'success',
})

const rootProps = reactiveOmit(props, ['variant'])

const classes = computed(() => {
  return {
    'sb-icon-circle': true,
    [`sb-icon-circle--${props.variant}`]: props.variant,
  }
})
</script>

<style scoped lang="scss">
$variants: ('success', 'warning', 'danger', 'info', 'highlight');

.sb-icon-circle {
  @each $variant in $variants {
    &--#{$variant} {
      color: var(--sb-color-icon-#{$variant}-bold);
      background-color: var(--sb-color-background-#{$variant}-subtle);
    }
  }

  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 48px;
  height: 48px;
  border-radius: $sb-border-radius-full;

  &.sb-icon-circle--neutral {
    color: var(--sb-color-icon-primary);
    background-color: var(--sb-color-background-secondary);
  }
}
</style>
